import { useMemo } from 'react';

import { useGatewayChatStream, type GatewayChatStreamConfig } from './useGatewayChatStream';
import {
  useGatewayConversationManager,
  type GatewayConversationManagerConfig,
} from './useGatewayConversationManager';
import { useGatewayPromptState, type GatewayPromptStateConfig } from './useGatewayPromptState';
import { useGatewaySessionAuth, type UseGatewaySessionAuthConfig } from './useGatewaySessionAuth';

export interface UseGatewayChatOptions {
  apiBase: string;
  authBase: string;
  sessionConfig?: UseGatewaySessionAuthConfig;
  streamConfig?: GatewayChatStreamConfig;
  conversationConfig?: GatewayConversationManagerConfig;
  promptConfig?: GatewayPromptStateConfig;
}

export const useGatewayChat = ({
  apiBase,
  authBase,
  sessionConfig,
  streamConfig,
  conversationConfig,
  promptConfig,
}: UseGatewayChatOptions) => {
  const session = useGatewaySessionAuth({ authBase, config: sessionConfig });

  const stream = useGatewayChatStream({
    gatewayApiBase: apiBase,
    ensureSession: session.ensureSession,
    authenticatedFetch: session.authenticatedFetch,
    config: streamConfig,
  });

  const conversations = useGatewayConversationManager({
    gatewayApiBase: apiBase,
    ensureSession: session.ensureSession,
    authenticatedFetch: session.authenticatedFetch,
    connectToConversation: stream.connectToConversation,
    config: conversationConfig,
  });

  const prompt = useGatewayPromptState({
    onSend: async ({ prompt, displayContent, file }) => {
      await stream.sendMessage({ prompt, displayContent, file });
    },
    config: promptConfig,
  });

  const value = useMemo(
    () => ({
      session,
      stream,
      conversations,
      prompt,
    }),
    [conversations, prompt, session, stream]
  );

  return value;
};
